/*
  Flowkeeper - Pomodoro timer for power users and teams
  Copyright (c) 2023 Constantine Kulak, Geoffrey Espagne

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU General Public License as published by
  the Free Software Foundation; either version 3 of the License, or
  (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU General Public License for more details.

  You should have received a copy of the GNU General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.
*/

import {AbstractDataItem} from "./abstract_data_item";
import {Backlog} from "./backlog";
import {Root} from "./root";
import {Origin} from "./abstract_stragegy";
import {Connection} from "./connection";

export class Workitem extends AbstractDataItem<Backlog> {
    public state: string|undefined;
    public name: string|undefined;

    public constructor(another?: Workitem, id?: string, parent?: Backlog, createDate?: Date, name?: string) {
        if(another) {
            super(another);
            this.state = another.state;
            this.name = another.name;
        }
        else {
            super(undefined, id, parent, createDate);
            this.state = 'new';
            this.name = name;
        }   
    }

    public clone(): Workitem {
        return new Workitem(this);
    }
}

// Strategies

export function CreateWorkitem(origin: Origin, data: Root, connection: Connection, seq: number, when: Date, who: string, ...params: string[]): void {
    const id = params[0];
    const parent = params[1];
    const name = params[2];
    const backlog = data.children.get(parent);
    if (!backlog) {
        throw new Error(`Attempt to create a workitem in a backlog ${parent} that does't exist`);
    }
    const workitem = backlog.children.get(id);
    if (workitem) {
        throw new Error(`Attempt to create a duplicate workitem ${id}`);
    }
    backlog.children.set(id, new Workitem(undefined, id, backlog, when, name));
}
CreateWorkitem.realName = 'CreateWorkitem'

export function DeleteWorkitem(origin: Origin, data: Root, connection: Connection, seq: number, when: Date, who: string, ...params: string[]): void {
    const id = params[0];
    let found = false;
    data.children.forEach(backlog => {
        const workitem = backlog.children.get(id);
        if (workitem) {
            backlog.children.delete(id);
            found = true;
        }
    });
    if (!found) {
        throw new Error(`Attempt to delete a workitem ${id} that doesn't exist`);
    }
}
DeleteWorkitem.realName = 'DeleteWorkitem'

export function RenameWorkitem(origin: Origin, data: Root, connection: Connection, seq: number, when: Date, who: string, ...params: string[]): void {
    const id = params[0];
    const name = params[1];
    let found = false;
    data.children.forEach(backlog => {
        const workitem = backlog.children.get(id);
        if (workitem) {
            workitem.name = name;
            found = true;
        }
    });
    if (!found) {
        throw new Error(`Attempt to rename a workitem ${id} that doesn't exist`);
    }
}
RenameWorkitem.realName = 'RenameWorkitem'

export function CompleteWorkitem(origin: Origin, data: Root, connection: Connection, seq: number, when: Date, who: string, ...params: string[]): void {
    const id = params[0];
    const state = params[1];
    let found = false;
    data.children.forEach(backlog => {
        const workitem = backlog.children.get(id);
        if (workitem) {
            workitem.state = state;
            found = true;
        }
    });
    if (!found) {
        throw new Error(`Attempt to complete a workitem ${id} that doesn't exist`);
    }
}
CompleteWorkitem.realName = 'CompleteWorkitem'
